import React from 'react';
import { Shield, Book, UserCheck, Upload, CreditCard, Ban, Scale, AlertTriangle, Building2, MessageSquare } from 'lucide-react';
import PolicySectionCard from '@/components/policy/PolicySectionCard';
import { motion } from 'framer-motion';
import PageHeader from '@/components/layout/PageHeader';
import PolicyLayout from '@/components/policy/PolicyLayout';

const TermsAndConditions = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <PolicyLayout>
      <PageHeader
        title="Terms & Conditions"
        description="Last updated: April 11, 2025"
        icon={<Scale className="w-8 h-8 text-primary" />}
      />

      {/* Cards Grid */}
      <motion.div 
        className="space-y-6"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={item}>
          <PolicySectionCard
            title="Agreement to Terms"
            icon={Book}
            variant="blue"
          >
            <p>By accessing or using Edaptica, you agree to be bound by these Terms. If you do not agree with any part of the Terms, you must not use the platform.</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Intellectual Property Rights"
            icon={Shield}
          >
            <p>Unless otherwise stated, all content on the platform—including source code, databases, design, software, audio, video, text, images, graphics, and trademarks ("Content" and "Marks")—is owned or licensed by us and is protected by copyright, trademark, and other applicable laws.</p>
            <p className="mt-4">You are granted a limited, non-transferable, non-exclusive license to access and use the platform for personal, non-commercial purposes only. Reproduction, redistribution, or commercial exploitation of the platform or its contents is strictly prohibited without prior written consent.</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="User Accounts"
            icon={UserCheck}
          >
            <div className="space-y-4">
              <h3 className="text-white font-semibold">3.1 Account Creation</h3>
              <p>To access certain features of the platform, you must create an account. You agree that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All information provided during registration is accurate, complete, and current</li>
                <li>You are at least 13 years old or have the consent of a parent or guardian</li>
              </ul>
              
              <h3 className="text-white font-semibold mt-6">3.2 Account Security</h3>
              <p>You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use or security breach.</p>
            </div>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="User Content"
            icon={Upload}
          >
            <p>Our platform may allow you to post or share content ("User Content"). You are solely responsible for the content you share, and by posting, you warrant that:</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>You own the rights or have permission to share the content</li>
              <li>It does not violate any laws, intellectual property, or privacy rights</li>
              <li>It is not defamatory, offensive, obscene, harmful, or misleading</li>
            </ul>
            <p className="mt-4">We reserve the right to remove any content that violates these Terms or is otherwise deemed inappropriate.</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Subscription Terms"
            icon={CreditCard}
          >
            <div className="space-y-4">
              <h3 className="text-white font-semibold">5.1 Billing</h3>
              <p>You agree to pay all applicable fees for subscription plans. We may revise pricing with 30 days' prior notice via email or through the platform.</p>
              
              <h3 className="text-white font-semibold">5.2 Automatic Renewal</h3>
              <p>All paid subscriptions automatically renew unless canceled before the end of the current billing cycle. You may cancel anytime via your account settings.</p>
              
              <h3 className="text-white font-semibold">5.3 Refunds</h3>
              <p>All refund-related matters are governed by our separate Refund & Cancellation Policy. Please refer to that document for details.</p>
            </div>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Prohibited Activities"
            icon={Ban}
          >
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Scrape, crawl, index, or extract data for commercial use</li>
              <li>Circumvent or disable security features</li>
              <li>Submit false support requests or misuse the support system</li>
              <li>Upload malware or malicious code</li>
              <li>Use the platform in any way that violates applicable laws</li>
              <li>Impersonate others or misrepresent your identity</li>
            </ul>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Limitation of Liability"
            icon={AlertTriangle}
          >
            <p>We shall not be liable for any indirect, incidental, or consequential damages, including but not limited to loss of profits, data, or business opportunities, arising from your use of the platform—even if we were advised of the possibility of such damages.</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Governing Law"
            icon={Building2}
          >
            <p>These Terms shall be governed by the laws of India. All disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Lucknow, Uttar Pradesh, India.</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Contact Us"
            icon={MessageSquare}
          >
            <div className="space-y-4">
              <p>If you have questions or concerns about these Terms, feel free to contact us:</p>
              <p>📧 Email: legal@edaptica.com</p>
            </div>
          </PolicySectionCard>
        </motion.div>
      </motion.div>

      {/* Footer */}
      <footer className="text-center text-sm text-muted-foreground pt-8 mt-8 border-t">
        © 2025 Cibeto Technologies Private Limited. All rights reserved.
      </footer>
    </PolicyLayout>
  );
};

export default TermsAndConditions;
